import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useMobile } from './hooks/useMobile';
import { useReducedMotion } from './hooks/useReducedMotion';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

gsap.registerPlugin(ScrollTrigger);

const badBadges = [
  'Missed Deadline',
  'Zero Engagement',
  'Over Budget',
  'Wrong Tone',
  'Inconsistent Posts',
  'Team Burnout',
];

const goodBadges = [
  '3.2x Engagement',
  'Always On-Brand',
  'Saves 20hrs/week',
  'Psychology-Backed',
  'Zero Missed Posts',
  'Grows With You',
];

const oldRoles = ['Strategist', 'Content Writer', 'Designer', 'Proofreader'];

export function ProblemSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const isMobile = useMobile();
  const prefersReduced = useReducedMotion();

  useEffect(() => {
    if (prefersReduced || !sectionRef.current) return;

    const ctx = gsap.context(() => {
      gsap.from('.problem-header > *', {
        opacity: 0,
        y: 30,
        stagger: 0.1,
        duration: 0.7,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.problem-header',
          start: 'top 85%',
          toggleActions: 'play none none none',
        },
      });
      // Panels use Framer Motion whileInView — no GSAP needed
    }, sectionRef);

    return () => ctx.revert();
  }, [isMobile, prefersReduced]);

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-landing-bg-alt"
      style={{
        paddingTop: 'clamp(80px, 10vh, 140px)',
        paddingBottom: 'clamp(80px, 10vh, 140px)',
      }}
    >
      <div className="relative z-10 mx-auto max-w-6xl px-6 lg:px-8">
        {/* Header — left-aligned for asymmetry */}
        <div className="problem-header mb-16 max-w-2xl">
          <p
            className="mb-4 text-zupa-purple-light"
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 'clamp(0.875rem, 1vw, 1.1rem)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.15em',
            }}
          >
            The Problem We Solve
          </p>
          <h2
            className="mb-4 text-[#E8E6F0]"
            style={{
              fontFamily: "'Space Grotesk', system-ui, sans-serif",
              fontSize: 'clamp(2rem, 4vw + 0.5rem, 3.75rem)',
              fontWeight: 600,
              lineHeight: 1.1,
            }}
          >
            Marketing is{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, #EF4444, #F97316)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              High Effort
            </span>
          </h2>
          <p
            className="max-w-[55ch] text-[#C8C6D2]"
            style={{
              fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
              fontSize: 'clamp(1.25rem, 1vw + 0.9rem, 1.5rem)',
              lineHeight: 1.65,
            }}
          >
            Five roles. Endless coordination. Still missing deadlines. Zupa replaces all of it.
          </p>
        </div>

        {/* Two-panel comparison */}
        <div className={cn('grid gap-6', isMobile ? 'grid-cols-1' : 'grid-cols-2')}>
          {/* The Old Way */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="chaos-panel p-8 lg:p-10"
            style={{
              background: 'linear-gradient(135deg, rgba(239,68,68,0.04), rgba(13,13,26,0.8))',
              border: '1px solid rgba(239,68,68,0.15)',
            }}
          >
            <h3
              className="mb-2 text-[#E8E6F0]"
              style={{
                fontFamily: "'Space Grotesk', system-ui, sans-serif",
                fontSize: 'clamp(1.5rem, 2.5vw, 2rem)',
                fontWeight: 600,
              }}
            >
              The Old Way
            </h3>
            <div className="mb-6 flex flex-wrap items-baseline gap-4">
              <span
                style={{
                  fontFamily: "'Space Grotesk', system-ui, sans-serif",
                  fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
                  fontWeight: 700,
                  color: '#EF4444',
                }}
              >
                $15,000+/month
              </span>
              <span
                className="text-[#C8C6D2]"
                style={{
                  fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
                  fontSize: '1.25rem',
                }}
              >
                10-14 days per campaign
              </span>
            </div>

            {/* Roles */}
            <div className="mb-6 space-y-2">
              {oldRoles.map((role) => (
                <div
                  key={role}
                  className="flex items-center gap-2 text-[#C8C6D2]"
                  style={{
                    fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
                    fontSize: '1.3rem',
                  }}
                >
                  <span className="text-[#EF4444]/60">x</span>
                  {role}
                </div>
              ))}
            </div>

            {/* Bad badges */}
            <div className="flex flex-wrap gap-2">
              {badBadges.map((badge) => (
                <span
                  key={badge}
                  className="px-4 py-2 text-base font-medium"
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    background: 'rgba(239, 68, 68, 0.08)',
                    border: '1px solid rgba(239, 68, 68, 0.2)',
                    color: '#F87171',
                  }}
                >
                  {badge}
                </span>
              ))}
            </div>
          </motion.div>

          {/* The Zupa Way */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.6, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="zupa-panel p-8 lg:p-10"
            style={{
              background: 'linear-gradient(135deg, rgba(124,58,237,0.06), rgba(13,13,26,0.8))',
              border: '1px solid rgba(124,58,237,0.2)',
            }}
          >
            <h3
              className="mb-2 text-[#E8E6F0]"
              style={{
                fontFamily: "'Space Grotesk', system-ui, sans-serif",
                fontSize: 'clamp(1.5rem, 2.5vw, 2rem)',
                fontWeight: 600,
              }}
            >
              The Zupa Way
            </h3>
            <div className="mb-6 flex flex-wrap items-baseline gap-4">
              <span
                style={{
                  fontFamily: "'Space Grotesk', system-ui, sans-serif",
                  fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
                  fontWeight: 700,
                  color: '#34D399',
                }}
              >
                From $49/month
              </span>
              <span
                className="text-[#C8C6D2]"
                style={{
                  fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
                  fontSize: '1.25rem',
                }}
              >
                Minutes per campaign
              </span>
            </div>

            {/* Zupa advantage description */}
            <p
              className="mb-6 text-[#C8C6D2]"
              style={{
                fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
                fontSize: '1.3rem',
                lineHeight: 1.6,
              }}
            >
              One tool. One dashboard. Psychology-backed content for every platform, generated in seconds.
            </p>

            {/* Good badges */}
            <div className="flex flex-wrap gap-2">
              {goodBadges.map((badge) => (
                <span
                  key={badge}
                  className="px-4 py-2 text-base font-medium"
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    background: 'rgba(52, 211, 153, 0.08)',
                    border: '1px solid rgba(52, 211, 153, 0.2)',
                    color: '#34D399',
                  }}
                >
                  {badge}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
